import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { Language, getTranslations } from '@/lib/i18n';

interface CustomerReviewsProps {
  language: Language;
}

const reviewers = [
  {
    id: 'review_sarah',
    name: 'Sarah M.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11d0bf777-1763295768849.png",
    alt: 'Smiling woman with dark hair in casual attire',
  },
  {
    id: 'review_david',
    name: 'David L.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13f205dcd-1763292852368.png",
    alt: 'Professional man with beard in business casual attire smiling',
  },
  {
    id: 'review_emily',
    name: 'Emily R.',
    rating: 5,
    image: "https://images.unsplash.com/photo-1629695211554-ec450ab8a1fe",
    alt: 'Young woman with blonde hair smiling in elegant setting',
  },
];

export default function CustomerReviews({ language }: CustomerReviewsProps) {
  const t = getTranslations(language);
  const r = t.home.reviews;

  const reviews = reviewers.map((reviewer, i) => ({
    ...reviewer,
    text: r.items[i]?.text ?? '',
    date: r.items[i]?.date ?? '',
  }));

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20 animate-fade-in">
          <span className="text-accent text-xs font-bold tracking-[0.4em] uppercase mb-6 block">
            {r.sectionLabel}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
            {r.title} <span className="italic font-light">{r.titleItalic}</span>
          </h2>
          <div className="flex items-center justify-center gap-3 mt-8">
            <div className="flex gap-1">
              {[...Array(5)]?.map((_, i) => (
                <Icon key={`star_${i}`} name="StarIcon" size={24} variant="solid" className="text-warning" />
              ))}
            </div>
            <span className="text-lg font-medium text-muted-foreground">{r.ratingText}</span>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-slide-up delay-200">
          {reviews?.map((review, index) => (
            <div
              key={review?.id}
              className="group bg-card rounded-3xl p-8 border border-border/50 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 space-y-6"
              style={{ animationDelay: `${index * 100}ms` }}>
              {/* Rating */}
              <div className="flex gap-1">
                {[...Array(review?.rating)]?.map((_, i) => (
                  <Icon key={`rating_${review?.id}_${i}`} name="StarIcon" size={18} variant="solid" className="text-warning" />
                ))}
              </div>
              {/* Review Text */}
              <p className="font-serif text-xl md:text-2xl leading-relaxed text-foreground italic break-words">
                "{review?.text}"
              </p>
              {/* Reviewer Info */}
              <div className="flex items-center gap-4 pt-4 border-t border-border/50">
                <div className="w-14 h-14 rounded-full overflow-hidden ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all flex-shrink-0">
                  <AppImage
                    src={review?.image}
                    alt={review?.alt}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                </div>
                <div>
                  <h4 className="font-bold text-base text-foreground">{review?.name}</h4>
                  <p className="text-sm text-muted-foreground">{review?.date}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <a
            href="https://www.tripadvisor.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors group">
            {r.readAllReviews}
            <Icon name="ArrowTopRightOnSquareIcon" size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
}